/**
 * CaseStatusHistory コンポーネント
 *
 * FL-142: 案件ステータス機能追加 - ステータス変更履歴表示コンポーネント
 * 案件ステータスの変更履歴を時系列で表示
 */

import React, { useState, useEffect } from 'react';
import { SupabaseServiceFactory } from '@/services/supabase';
import CaseStatusBadge from './CaseStatusBadge';

interface CaseStatusHistoryRecord {
  id: string;
  caseId: string;
  previousStatus: string | null;
  newStatus: string;
  changedBy: string | null;
  notes: string | null;
  createdAt: string;
}

interface CaseStatusHistoryProps {
  /** 案件 ID */
  caseId: string;
  /** テーブルサイズ */
  size?: 'sm' | 'md' | 'lg';
  /** 追加クラス名 */
  className?: string;
}

/**
 * CaseStatusHistory コンポーネント
 *
 * 案件のステータス変更履歴を時系列テーブル形式で表示するコンポーネント
 * 最新の変更から過去の変更に向かって表示（降順）
 *
 * @example
 * <CaseStatusHistory caseId="case-123" />
 *
 * @example
 * // サイズ指定
 * <CaseStatusHistory caseId="case-123" size="sm" />
 */
export const CaseStatusHistory: React.FC<CaseStatusHistoryProps> = ({
  caseId,
  size = 'md',
  className = '',
}) => {
  const [history, setHistory] = useState<CaseStatusHistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // ステータス履歴を取得
  useEffect(() => {
    const fetchStatusHistory = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const caseService = SupabaseServiceFactory.getCaseService();
        const historyData = await caseService.getCaseStatusHistory(caseId);
        setHistory(historyData);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('ステータス履歴の取得に失敗しました');
        setError(error);
        console.error('❌ ステータス履歴取得エラー:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatusHistory();
  }, [caseId]);

  // 日時をフォーマット
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // ローディング状態
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className='text-center'>
          <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
          <p className='mt-4 text-gray-600 dark:text-gray-400'>読み込み中...</p>
        </div>
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div
        className={`p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-700 ${className}`}
      >
        <p className='text-red-700 dark:text-red-300 font-medium'>
          {error.message || 'ステータス履歴の取得に失敗しました'}
        </p>
      </div>
    );
  }

  // 空の状態
  if (history.length === 0) {
    return (
      <div
        className={`p-8 text-center bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-900/20 dark:border-gray-700 ${className}`}
      >
        <p className='text-gray-500 dark:text-gray-400'>ステータス変更の履歴がありません</p>
      </div>
    );
  }

  // テーブルサイズクラス
  const tableSizeClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';

  const headerSizeClass = size === 'sm' ? 'py-2 px-3' : size === 'lg' ? 'py-4 px-4' : 'py-3 px-4';

  const cellSizeClass = size === 'sm' ? 'py-2 px-3' : size === 'lg' ? 'py-4 px-4' : 'py-3 px-4';

  return (
    <div className='overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700'>
      <table className={`w-full ${tableSizeClass} bg-white dark:bg-gray-800 ${className}`}>
        <thead>
          <tr className='bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
            <th
              className={`${headerSizeClass} text-left font-semibold text-gray-700 dark:text-gray-300`}
            >
              日時
            </th>
            <th
              className={`${headerSizeClass} text-left font-semibold text-gray-700 dark:text-gray-300`}
            >
              前のステータス
            </th>
            <th
              className={`${headerSizeClass} text-left font-semibold text-gray-700 dark:text-gray-300`}
            >
              新しいステータス
            </th>
            <th
              className={`${headerSizeClass} text-left font-semibold text-gray-700 dark:text-gray-300`}
            >
              変更者
            </th>
            <th
              className={`${headerSizeClass} text-left font-semibold text-gray-700 dark:text-gray-300`}
            >
              変更理由
            </th>
          </tr>
        </thead>
        <tbody>
          {history.map((record, index) => (
            <tr
              key={record.id}
              className={`border-b border-gray-200 dark:border-gray-700 ${
                index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/30'
              } hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <td className={`${cellSizeClass} text-gray-900 dark:text-gray-100`}>
                {formatDateTime(record.createdAt)}
              </td>
              <td className={`${cellSizeClass}`}>
                {record.previousStatus ? (
                  <CaseStatusBadge status={record.previousStatus as any} size={size} />
                ) : (
                  <span className='text-gray-500 dark:text-gray-400'>-</span>
                )}
              </td>
              <td className={`${cellSizeClass}`}>
                <CaseStatusBadge status={record.newStatus as any} size={size} />
              </td>
              <td className={`${cellSizeClass} text-gray-900 dark:text-gray-100`}>
                {record.changedBy || '-'}
              </td>
              <td className={`${cellSizeClass} text-gray-700 dark:text-gray-300`}>
                {record.notes && record.notes.trim() ? (
                  <span title={record.notes} className='break-words'>
                    {record.notes}
                  </span>
                ) : (
                  <span className='text-gray-500 dark:text-gray-400'>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CaseStatusHistory;
